import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { fetchCadeConselhoCases } from '@/services/cade';
import type { ConselhoItemMetric, ConselhoMemberItem } from '@/services/types';

const METRICS: ConselhoItemMetric[] = ['follow', 'outvoted', 'pair'];

/** The link every bench figure uses to open the items behind it. */
export function conselhoCasesHref(metric: ConselhoItemMetric, name: string, other?: string | null): string {
  const query = new URLSearchParams({ metric, name });
  if (other) query.set('other', other);
  return `/cade/conselho/cases?${query.toString()}`;
}

/**
 * The complete items behind ONE figure on the CADE Tribunal bench or a
 * member's page (no orphan numbers): follow-the-relator, outvoted, or a pair.
 *
 * Reached by clicking a figure, never from the menu. The API answers only
 * when its list holds exactly the n the figure states, so the count on this
 * page and the figure it came from cannot disagree.
 */
export function CadeConselhoCasesPage() {
  const { t, i18n } = useTranslation(['cade', 'common']);
  const [params] = useSearchParams();
  const name = params.get('name') ?? '';
  const other = params.get('other');
  const raw = params.get('metric') ?? '';
  const metric = (METRICS as string[]).includes(raw) ? (raw as ConselhoItemMetric) : null;
  const valid = name !== '' && metric !== null && (metric !== 'pair' || Boolean(other));
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'conselho', 'cases', metric, name, other],
    queryFn: () => fetchCadeConselhoCases({ name, metric: metric as ConselhoItemMetric, other }),
    enabled: valid,
  });

  if (!valid) {
    return (
      <div className="p-6">
        <ErrorMessage message={t('cade:conselhoCases.invalid')} />
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={8} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const number = new Intl.NumberFormat(i18n.language);
  const side = (value: string | null) => (value ? t(`cade:conselhoCases.side.${value}`, value) : '—');
  const relatorSide = (value: string | null) =>
    value ? t(`cade:conselhoCases.relatorSide.${value}`, value) : '—';

  return (
    <div className="space-y-4 p-6">
      <Link
        to="/cade/conselho"
        className="inline-flex items-center gap-1 text-xs text-primary underline-offset-4 hover:underline"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        {t('cade:conselhoCases.back')}
      </Link>
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t(`cade:conselhoCases.title.${metric}`)}</h1>
        <p className="text-sm text-foreground" data-testid="cases-subject">
          {other ? `${name} — ${other}` : name}
        </p>
        <p className="text-sm text-muted-foreground">{t(`cade:conselhoCases.help.${metric}`)}</p>
      </header>

      <AsOfStrip asOf={data.as_of} />

      <Answer answer={data.items} scope="record">
        {(value) => (
          <div className="space-y-2">
            <p className="text-sm text-foreground" data-testid="cases-count">
              {t(`cade:conselhoCases.count.${metric}`, {
                counted: number.format(value.counted),
                total: number.format(value.items.length),
              })}
            </p>
            <ItemsTable items={value.items} pair={metric === 'pair'} side={side} relatorSide={relatorSide} />
          </div>
        )}
      </Answer>
    </div>
  );
}

function ItemsTable({
  items,
  pair,
  side,
  relatorSide,
}: {
  items: ConselhoMemberItem[];
  pair: boolean;
  side: (v: string | null) => string;
  relatorSide: (v: string | null) => string;
}) {
  const { t } = useTranslation(['cade']);
  return (
    <div className="max-h-[36rem] overflow-auto">
      <table className="w-full text-xs" data-testid="cases-table">
        <thead className="sticky top-0 z-10 bg-muted text-left">
          <tr>
            <th className="p-2">{t('cade:conselhoCases.columns.year')}</th>
            <th className="p-2">{t('cade:conselhoCases.columns.case')}</th>
            <th className="p-2">{t('cade:conselhoCases.columns.item')}</th>
            <th className="p-2">{t('cade:conselhoCases.columns.side')}</th>
            {pair ? <th className="p-2">{t('cade:conselhoCases.columns.otherSide')}</th> : null}
            <th className="p-2">{t('cade:conselhoCases.columns.relator')}</th>
            <th className="p-2">{t('cade:conselhoCases.columns.relatorSide')}</th>
            <th className="p-2">{t('cade:conselhoCases.columns.contested')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            // The list is server-ordered and never reordered here; one case can
            // sit several sessions under one item number, so the index is the key.
            <tr
              key={i}
              className={`border-t ${item.counted ? '' : 'text-muted-foreground'}`}
              data-counted={item.counted ? 'true' : 'false'}
            >
              <td className="p-2 tabular-nums">{item.year ?? '—'}</td>
              <td className="p-2">
                {item.case_key && item.process_number ? (
                  <Link to={`/cade/c/${item.case_key}`} className="underline-offset-4 hover:underline">
                    {item.process_number}
                  </Link>
                ) : (
                  item.process_number ?? '—'
                )}
              </td>
              <td className="p-2 tabular-nums">{item.item_number}</td>
              <td className="p-2">{side(item.side)}</td>
              {pair ? <td className="p-2">{side(item.other_side)}</td> : null}
              <td className="p-2">{item.relator ?? '—'}</td>
              <td className="p-2">{relatorSide(item.relator_side)}</td>
              <td className="p-2">{item.contested ? t('cade:conselhoCases.yes') : t('cade:conselhoCases.no')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
